import { ApiException } from "@/types/api"
import { mockSmithersClientService } from "@/mocks/smithers-client-service"
import { getMockProVenueInstance, mockProVenueInstanceService } from "@/mocks/provenue-instance-service"
import { getMockSmithersGlobal } from "@/mocks/smithers-global-service"
import { compareSmithersDevices } from "@/lib/smithers-device-columns"
import { validateSmithersBulkDeviceIds } from "@/lib/smithers-bulk"
import { validateSmithersBulkUpdateRequest } from "@/lib/smithers-bulk-update"
import { getSmithersProVenueSelection, toSmithersProVenueRequest } from "@/lib/smithers-provenue"
import type { BulkUpdateSmithersDevicesRequest, BulkUpdateSmithersDevicesResult } from "@/types/smithers-device"
import { toSmithersImportRequest, validateSmithersBulkImportFile } from "@/lib/smithers-bulk-import"
import type { SmithersBulkImportJobStatusResponse, SmithersBulkImportUploadResult, SmithersBulkImportValidationResult } from "@/types/smithers-bulk-import"

interface MockSmithersImportJob {
  response: SmithersBulkImportJobStatusResponse
  validation: SmithersBulkImportValidationResult
  pollCount: number
  processing?: Promise<void>
}
const importJobs = new Map<string, MockSmithersImportJob>()
let nextImportJobId = 1
import type { BulkDeleteSmithersDevicesResult, CreateSmithersDeviceRequest, UpdateSmithersDeviceRequest, SmithersDeviceListParams, SmithersDevicePage, SmithersDeviceConfig } from "@/types/smithers-device"

const devices: SmithersDeviceConfig[] = [
  {
    id: 82,
    deviceId: "gae-view-global",
    sotiDeviceId: "",
    clientId: "HOU",
    createdAt: "2026-10-06T16:18:44.660081Z",
    inheritedFromGlobal: [
      "validationVendor", "facilityCode", "venueId", "areaCode", "deviceCode",
      "nteClubCode", "matchThreshold", "validationTimeoutMs", "proVenueInstanceId",
    ],
    requiresProVenueInstance: true,
    validationVendor: "ProVenue",
    facilityCode: "FAC1",
    venueId: "VEN1",
    areaCode: "A2",
    deviceCode: "D1",
    nteClubCode: "NTE1",
    matchThreshold: 85,
    validationTimeoutMs: 3000,
    proVenueInstanceId: null,
    proVenueUser: "",
    proVenueInstances: [],
    updatedAt: "2026-10-06T16:18:44.660081Z",
    updatedBy: "device-config-svc@qa",
  },
]

const globalDevice = devices[0]
devices.push(
  {
    ...globalDevice,
    id: 6,
    deviceId: "gae-003",
    sotiDeviceId: "soti-002",
    inheritedFromGlobal: ["areaCode", "deviceCode"],
    validationVendor: "Orion",
    facilityCode: "",
    venueId: "VEN2",
    areaCode: "A1",
    nteClubCode: "NTE2",
    matchThreshold: 0,
    validationTimeoutMs: 4500,
    proVenueInstanceId: 2,
    proVenueUser: "Sandbox",
    proVenueInstances: [{ name: "Sandbox", hostName: "https://sandbox.pvapi.provenue.com" }],
    createdAt: "2026-10-05T20:37:38.989697Z",
    updatedAt: "2026-10-05T20:37:38.989697Z",
  },
  {
    ...globalDevice,
    id: 83,
    deviceId: "gae-nyy-001",
    sotiDeviceId: "soti-nyy-001",
    clientId: "NYY",
    proVenueInstanceId: 2,
    proVenueUser: "Sandbox",
    proVenueInstances: [{ name: "Sandbox", hostName: "https://sandbox.pvapi.provenue.com" }],
  },
  {
    ...globalDevice,
    id: 84,
    deviceId: "gae-lad-001",
    sotiDeviceId: "soti-lad-001",
    clientId: "LAD",
    inheritedFromGlobal: ["matchThreshold", "validationTimeoutMs"],
    requiresProVenueInstance: false,
    facilityCode: "FAC3",
    venueId: "VEN3",
    areaCode: "A3",
    deviceCode: "D3",
    nteClubCode: "NTE3",
  },
)

export const mockSmithersDeviceService = {
  async bulkCreate(file: File): Promise<SmithersBulkImportUploadResult> {
    const clients = await mockSmithersClientService.getAll()
    const validation = await validateSmithersBulkImportFile(file, { validClientIds: new Set(clients.map((client) => client.clientId)) })
    const fatal = validation.issues.filter((issue) => issue.rowNumber === undefined || issue.field === "clientId")
    if (fatal.length) throw new ApiException({ code: file.size > 10 * 1024 * 1024 || validation.rows.length > 10_000 ? 413 : 400, message: fatal.map((issue) => issue.message).join(" ") })
    const createdAt = new Date().toISOString()
    const result: SmithersBulkImportUploadResult = { jobId: `mock-smithers-bulk-job-${nextImportJobId++}`, status: "PENDING", totalRows: validation.rows.length }
    const failedRows = new Map(validation.issues.flatMap((issue) => issue.rowNumber === undefined ? [] : [[issue.rowNumber, issue.message] as const]))
    const errorSample = [...failedRows].sort(([a], [b]) => a - b).map(([rowNumber, error]) => ({ rowNumber, error, deviceId: validation.rows[rowNumber - 2].deviceId, clientId: validation.rows[rowNumber - 2].clientId }))
    importJobs.set(result.jobId, { validation, pollCount: 0, response: { ...result, processed: failedRows.size, succeeded: 0, failed: failedRows.size, skippedDuplicate: 0, errorSample, createdBy: "mock-user", createdAt, updatedAt: createdAt } })
    return result
  },
  async getBulkImportJob(jobId: string): Promise<SmithersBulkImportJobStatusResponse> {
    const job = importJobs.get(jobId)
    if (!job) throw new ApiException({ code: 404, message: "Smithers bulk import job not found" })
    job.pollCount++
    if (job.pollCount === 1) {
      job.response.status = "PROCESSING"
      job.response.startedAt = new Date().toISOString()
    } else {
      job.processing ??= processImportJob(job)
      await job.processing
    }
    return structuredClone(job.response)
  },
  async getAll(params: SmithersDeviceListParams = {}): Promise<SmithersDevicePage> {
    return listDevices(devices, params)
  },
  async getByClient(id: number, params: SmithersDeviceListParams = {}): Promise<SmithersDevicePage> {
    const client = await mockSmithersClientService.getById(id)
    return listDevices(devices.filter((device) => device.clientId === client.clientId), params)
  },
  async create(data: CreateSmithersDeviceRequest): Promise<SmithersDeviceConfig> {
    const clientId = data.clientId.trim()
    const deviceId = data.deviceId.trim()
    if (!clientId || !deviceId || Array.from(clientId).length > 100 || Array.from(deviceId).length > 100) throw new ApiException({ code: 400, message: "Invalid clientId or deviceId" })
    if (devices.some((device) => device.clientId === clientId && device.deviceId === deviceId)) throw new ApiException({ code: 409, message: "smithers device id conflict" })
    if ((data.matchThreshold !== undefined && (!Number.isInteger(data.matchThreshold) || data.matchThreshold < 0 || data.matchThreshold > 2147483647)) || (data.validationTimeoutMs !== undefined && (!Number.isSafeInteger(data.validationTimeoutMs) || data.validationTimeoutMs < 0))) throw new ApiException({ code: 400, message: "Invalid numeric configuration" })
    if (data.validationVendor !== undefined && data.validationVendor !== "ProVenue" && data.validationVendor !== "Orion") throw new ApiException({ code: 400, message: "Invalid validationVendor" })
    const proVenue = await resolveProVenueSelection(data)
    const now = new Date().toISOString()
    const device: SmithersDeviceConfig = {
      ...structuredClone(globalDevice), ...getMockSmithersGlobal(), id: Math.max(0, ...devices.map((item) => item.id)) + 1,
      clientId, deviceId, sotiDeviceId: data.sotiDeviceId.trim(), createdAt: now, updatedAt: now, updatedBy: "mock-user",
      inheritedFromGlobal: [],
    }
    for (const key of ["validationVendor", "facilityCode", "venueId", "areaCode", "deviceCode", "nteClubCode", "matchThreshold", "validationTimeoutMs"] as const) {
      if (data[key] === undefined) device.inheritedFromGlobal.push(key)
      else Object.assign(device, { [key]: data[key] })
    }
    applyProVenueSelection(device, proVenue)
    if (!(await mockSmithersClientService.getAll()).some((client) => client.clientId === clientId)) await mockSmithersClientService.create({ clientId })
    devices.push(device)
    return resolveDevice(device)
  },
  async update(id: number, data: UpdateSmithersDeviceRequest): Promise<SmithersDeviceConfig> {
    const index = devices.findIndex((device) => device.id === id)
    if (index === -1) throw new ApiException({ code: 404, message: "Smithers device not found" })
    if ((data.matchThreshold !== undefined && (!Number.isInteger(data.matchThreshold) || data.matchThreshold < 0 || data.matchThreshold > 2147483647)) || (data.validationTimeoutMs !== undefined && (!Number.isSafeInteger(data.validationTimeoutMs) || data.validationTimeoutMs < 0))) throw new ApiException({ code: 400, message: "Invalid numeric configuration" })
    if (data.validationVendor !== undefined && data.validationVendor !== "ProVenue" && data.validationVendor !== "Orion") throw new ApiException({ code: 400, message: "Invalid validationVendor" })
    const proVenue = await resolveProVenueSelection(data)
    const next = { ...devices[index], ...getMockSmithersGlobal(), inheritedFromGlobal: [] as string[], updatedAt: new Date().toISOString(), updatedBy: "mock-user" }
    for (const key of ["validationVendor", "facilityCode", "venueId", "areaCode", "deviceCode", "nteClubCode", "matchThreshold", "validationTimeoutMs"] as const) {
      if (data[key] === undefined) next.inheritedFromGlobal.push(key)
      else Object.assign(next, { [key]: data[key] })
    }
    applyProVenueSelection(next, proVenue)
    devices[index] = next
    return resolveDevice(next)
  },
  async delete(id: number): Promise<void> {
    const index = devices.findIndex((device) => device.id === id)
    if (index === -1) throw new ApiException({ code: 404, message: "Smithers device not found" })
    devices.splice(index, 1)
  },
  async bulkDelete(ids: number[]): Promise<BulkDeleteSmithersDevicesResult> {
    validateSmithersBulkDeviceIds(ids)
    const result: BulkDeleteSmithersDevicesResult = { results: [], deleted: 0, failed: 0 }
    for (const id of ids) {
      const device = devices.find((item) => item.id === id)
      if (!device) {
        result.results.push({ id, status: "failed", error: "device not found" })
        result.failed++
      } else {
        devices.splice(devices.indexOf(device), 1)
        result.results.push({ id, status: "deleted", clientId: device.clientId, deviceId: device.deviceId })
        result.deleted++
      }
    }
    return result
  },
  async bulkUpdate(data: BulkUpdateSmithersDevicesRequest): Promise<BulkUpdateSmithersDevicesResult> {
    validateSmithersBulkUpdateRequest(data)
    const result: BulkUpdateSmithersDevicesResult = { requestedCount: data.ids.length, successCount: 0, failureCount: 0, failedDevices: [] }
    const { update } = data
    for (const id of data.ids) {
      const device = devices.find((item) => item.id === id)
      if (!device) {
        result.failedDevices.push({ id, status: 404, code: "device_not_found", message: "Smithers device was not found" })
        continue
      }
      const overrides: UpdateSmithersDeviceRequest = { ...toSmithersProVenueRequest(getSmithersProVenueSelection(device)) }
      for (const key of ["validationVendor", "facilityCode", "venueId", "areaCode", "deviceCode", "nteClubCode", "matchThreshold", "validationTimeoutMs"] as const) {
        if (!device.inheritedFromGlobal.includes(key)) Object.assign(overrides, { [key]: device[key] })
      }
      if (update.requiresProVenueInstance !== undefined) {
        overrides.requiresProVenueInstance = update.requiresProVenueInstance
        delete overrides.proVenueInstanceId
        if (update.proVenueInstanceId != null) overrides.proVenueInstanceId = update.proVenueInstanceId
      } else {
        const [field, value] = Object.entries(update)[0] as [Exclude<keyof UpdateSmithersDeviceRequest, "requiresProVenueInstance">, unknown]
        delete overrides[field]
        if (value !== null) Object.assign(overrides, { [field]: value })
      }
      try { await mockSmithersDeviceService.update(id, overrides); result.successCount++ } catch (error) {
        const invalid = error instanceof ApiException && (error.code === 400 || error.code === 404)
        result.failedDevices.push({ id, status: invalid ? 400 : 500, code: invalid ? "invalid_device_configuration" : "internal_error", message: invalid ? error.message : "Device configuration was not updated" })
      }
    }
    result.failureCount = result.failedDevices.length
    return result
  },
  async getById(id: number): Promise<SmithersDeviceConfig> {
    const device = devices.find((item) => item.id === id)
    if (!device) throw new ApiException({ code: 404, message: "Smithers device not found" })
    return resolveDevice(device)
  },
}

async function processImportJob(job: MockSmithersImportJob): Promise<void> {
  const preFailedRows = new Set(job.response.errorSample.map((failure) => failure.rowNumber))
  for (const [index, row] of job.validation.rows.entries()) {
    const rowNumber = index + 2
    if (preFailedRows.has(rowNumber)) continue
    try {
      if (!(await mockSmithersClientService.getAll()).some((client) => client.clientId === row.clientId)) throw new Error(`Unknown clientId "${row.clientId}"`)
      await mockSmithersDeviceService.create(toSmithersImportRequest(row))
      job.response.succeeded++
    } catch (error) {
      job.response.failed++
      job.response.errorSample.push({ rowNumber, deviceId: row.deviceId, clientId: row.clientId, error: error instanceof Error ? error.message : "Unable to create device" })
    }
    job.response.processed++
  }
  job.response.status = job.response.failed ? "COMPLETED_WITH_ERRORS" : "COMPLETED"
  job.response.errorSample.sort((a, b) => a.rowNumber - b.rowNumber)
  job.response.updatedAt = job.response.finishedAt = new Date().toISOString()
}

export function hasMockSmithersDeviceUsingProVenueInstance(id: number): boolean {
  return devices.some((device) => device.requiresProVenueInstance && !device.inheritedFromGlobal.includes("proVenueInstanceId") && device.proVenueInstanceId === id)
}

interface MockProVenueSelection {
  requires: boolean
  instance?: { id: number; name: string; hostName: string }
}

async function resolveProVenueSelection(data: Pick<UpdateSmithersDeviceRequest, "requiresProVenueInstance" | "proVenueInstanceId">): Promise<MockProVenueSelection> {
  if (typeof data.requiresProVenueInstance !== "boolean") throw new ApiException({ code: 400, message: "requiresProVenueInstance is required and must be true or false" })
  if (!data.requiresProVenueInstance || data.proVenueInstanceId === undefined) return { requires: data.requiresProVenueInstance }
  const instance = (await mockProVenueInstanceService.getAll()).find((item) => item.id === data.proVenueInstanceId)
  if (!instance) throw new ApiException({ code: 404, message: "proVenue instance not found" })
  return { requires: true, instance }
}

function applyProVenueSelection(device: SmithersDeviceConfig, { requires, instance }: MockProVenueSelection): void {
  device.requiresProVenueInstance = requires
  if (instance) {
    device.proVenueInstanceId = instance.id
    device.proVenueUser = instance.name
    device.proVenueInstances = [{ name: instance.name, hostName: instance.hostName }]
  } else if (!requires) {
    device.proVenueInstanceId = null
    device.proVenueUser = ""
    device.proVenueInstances = []
  } else device.inheritedFromGlobal.push("proVenueInstanceId")
}

function listDevices(source: SmithersDeviceConfig[], params: SmithersDeviceListParams): SmithersDevicePage {
  const { page = 1, pageSize = 50, filter = "", sortBy = "deviceId", sortOrder = "asc" } = params
  const term = filter.toLowerCase()
  const matches = source.map(resolveDevice).filter((device) => [device.deviceId, device.sotiDeviceId, device.clientId, device.facilityCode, device.venueId, device.deviceCode, device.areaCode, device.nteClubCode, device.proVenueUser, device.proVenueInstances[0]?.hostName ?? ""].some((value) => value.toLowerCase().includes(term)))
    .sort((a, b) => {
      const comparison = sortBy === "proVenueInstanceId" ? (a.proVenueInstanceId ?? -1) - (b.proVenueInstanceId ?? -1) : compareSmithersDevices(a, b, sortBy)
      return (sortOrder === "asc" ? comparison : -comparison) || a.id - b.id
    })
  return structuredClone({ items: matches.slice((page - 1) * pageSize, page * pageSize), total: matches.length, page, pageSize })
}

function resolveDevice(device: SmithersDeviceConfig): SmithersDeviceConfig {
  const resolved = structuredClone(device)
  const global = getMockSmithersGlobal()
  for (const key of ["validationVendor", "facilityCode", "venueId", "areaCode", "deviceCode", "nteClubCode", "matchThreshold", "validationTimeoutMs"] as const) {
    if (device.inheritedFromGlobal.includes(key)) Object.assign(resolved, { [key]: global[key] })
  }
  if (device.inheritedFromGlobal.includes("proVenueInstanceId")) {
    resolved.proVenueInstanceId = global.proVenueInstanceId
  }
  const instance = getMockProVenueInstance(resolved.proVenueInstanceId)
  resolved.proVenueUser = instance?.name ?? ""
  resolved.proVenueInstances = instance ? [{ name: instance.name, hostName: instance.hostName }] : []
  return resolved
}
